import type { QueryClient } from '@tanstack/react-query'
import { Outlet, createRootRouteWithContext, useRouterState } from '@tanstack/react-router'
import { AdminBar } from '../components/AdminBar'
import { CommandPalette } from '../components/CommandPalette'

export type RouterContext = {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
})

function RootLayout() {
  // The palette searches the public catalog, so it is not offered inside the
  // admin panel, which has its own game search on the add-keys page.
  const isAdmin = useRouterState({
    select: (state) => state.location.pathname.startsWith('/admin'),
  })

  return (
    <div className="min-h-dvh bg-surface-0 text-ink">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-lg focus:bg-accent focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-surface-0"
      >
        Skip to content
      </a>

      {isAdmin ? null : <AdminBar />}

      <Outlet />

      {isAdmin ? null : <CommandPalette />}
    </div>
  )
}

function NotFound() {
  return (
    <div className="grain flex min-h-dvh flex-col items-center justify-center gap-3 px-6 text-center">
      {/*
        The dragon, with the colour drained out of it.

        Everywhere else it sits on a tinted tile and carries the site's green.
        Here it is loose on the page and grey, which says "there is nothing at
        this address" before the heading gets a chance to — and keeps the one
        green thing on screen being the way out.
      */}
      {/*
        No entrance animation on it. `pop-in` ends at full opacity, so pairing
        it with a dimmed mark meant fading up to 100% and then snapping back
        down to 60% when the animation handed control back to the stylesheet.
      */}
      <img
        src="/favicon.svg"
        alt=""
        className="mb-4 size-28 opacity-60 grayscale sm:size-32"
      />
      <h1 className="font-display text-2xl font-semibold tracking-tight">Nothing here</h1>
      <p className="max-w-xs text-sm text-ink-soft">
        The dragon is sitting on an empty page. Whatever was here has been given
        away, or never existed.
      </p>
      <a
        href="/"
        className="mt-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-surface-0 transition-colors hover:bg-accent-strong"
      >
        Back to the library
      </a>
    </div>
  )
}
